import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

type EmptyStateProps = {
  title: ReactNode;
  /** One line saying what to do next. */
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  size?: 'default' | 'compact';
  className?: string;
};

/** What a list shows when it has nothing: title, help line, optional action. */
function EmptyState({
  title,
  description,
  icon,
  action,
  size = 'default',
  className,
}: EmptyStateProps) {
  return (
    <div
      data-slot="empty-state"
      data-size={size}
      className={cn(
        'flex flex-col items-center gap-2 rounded-lg border border-dashed text-center',
        size === 'compact' ? 'px-4 py-6' : 'px-6 py-12',
        className,
      )}
    >
      {icon ? (
        <div aria-hidden="true" className="mb-1 text-muted-foreground [&_svg]:size-8">
          {icon}
        </div>
      ) : null}
      <p className={cn('font-bold text-heading', size === 'compact' ? 'text-sm' : 'text-base')}>
        {title}
      </p>
      {description ? (
        <p className="max-w-prose text-sm text-muted-foreground">{description}</p>
      ) : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

export { EmptyState, type EmptyStateProps };
